import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaDownload, FaGamepad, FaLeaf, FaMobileAlt, FaShieldAlt, FaTrophy } from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

const featureCards = [
  {
    icon: FaMobileAlt,
    title: 'Smooth Controls',
    description: 'Responsive touch input designed for quick taps, precise timing, and satisfying movement.'
  },
  {
    icon: FaGamepad,
    title: 'Endless Gameplay',
    description: 'Keep hopping across floating leaves in an endless run built to stay fresh and challenging.'
  },
  {
    icon: FaLeaf,
    title: 'Cute Frog Character',
    description: 'A playful frog hero brings charm and personality to every jump, bounce, and near miss.'
  },
  {
    icon: FaTrophy,
    title: 'High Score Challenge',
    description: 'Compete with yourself and friends to push farther, react faster, and beat your best score.'
  }
];

const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.RedRender.JumpFroggo';
const heroImage = '/jumpfroggo-banner.png';

const gameplayHighlights = [
  {
    title: 'Smooth Controls',
    description: 'Easy to play, hard to master'
  },
  {
    title: 'Endless Challenge',
    description: 'No limits, keep jumping higher'
  },
  {
    title: 'High Score System',
    description: 'Beat your best score every time'
  }
];

const JumpFroggo: React.FC = () => {
  const jumpFroggoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: 'JumpFroggo',
    url: `${siteUrl}/jumpfroggo`,
    image: `${siteUrl}${heroImage}`,
    gamePlatform: 'Android',
    applicationCategory: 'Game',
    description: 'JumpFroggo is a playful Android frog jumping game focused on smooth controls, endless gameplay, and high-score challenges.'
  };

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black pt-24">
      <SEO
        title="JumpFroggo - Android Frog Jump Game by RedRender"
        description="JumpFroggo is a fun Android frog jump game by RedRender with smooth controls, endless gameplay, a cute frog character, and high-score challenges."
        path="/jumpfroggo"
        image={heroImage}
        keywords={[
          'JumpFroggo',
          'Android game',
          'frog jump game',
          'mobile game development',
          'RedRender game'
        ]}
        jsonLd={jumpFroggoJsonLd}
      />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:72px_72px] pointer-events-none" />
      <div className="absolute top-24 left-[-8rem] h-72 w-72 rounded-full bg-red-500/20 blur-3xl pointer-events-none" />
      <div className="absolute right-[-6rem] top-[28rem] h-80 w-80 rounded-full bg-red-600/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-red-500/10 blur-3xl pointer-events-none" />

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 relative z-10">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                </span>
                <span className="font-display text-sm font-semibold text-red-400">New Mobile Game Landing Page</span>
              </div>

              <h1 className="mt-8 max-w-4xl font-display text-5xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl">
                JumpFroggo
                <span className="mt-2 block bg-gradient-to-r from-red-500 via-red-300 to-red-500 bg-[length:200%_100%] bg-clip-text text-transparent animate-gradient">
                  Frog Jump Game
                </span>
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-400 sm:text-xl">
                Jump across floating leaves and achieve the highest score with a charming frog adventure built for
                quick sessions and addictive replayability.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <a
                  href={playStoreUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-red-400/40 bg-gradient-to-r from-red-500 via-red-500 to-red-600 px-8 py-4 font-semibold text-white shadow-[0_0_30px_rgba(239,68,68,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(239,68,68,0.5)]"
                >
                  <FaDownload className="transition-transform duration-300 group-hover:scale-110" />
                  <span>Download on Play Store</span>
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <div className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-8 py-4 font-semibold text-white backdrop-blur-sm">
                  Premium arcade gameplay
                </div>
                <Link
                  to="/jumpfroggo-privacy-policy"
                  className="inline-flex items-center justify-center gap-3 rounded-full border border-cyan-300/30 bg-cyan-400/10 px-8 py-4 font-semibold text-cyan-100 backdrop-blur-sm transition-all duration-300 hover:border-cyan-200/60 hover:text-white"
                >
                  <FaShieldAlt />
                  <span>Privacy Policy</span>
                </Link>
              </div>

              <div className="mt-10 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4">
                {['Arcade Fun', 'Leaf Hopping', 'Endless Mode', 'Score Chase'].map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-center text-sm font-medium text-gray-300 backdrop-blur-sm"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="relative min-h-[420px] animate-scale-in sm:min-h-[520px] lg:min-h-[640px]">
              <div className="absolute inset-0 rounded-[2.5rem] bg-[radial-gradient(circle_at_62%_38%,rgba(255,85,85,0.18),transparent_16%),radial-gradient(circle_at_58%_52%,rgba(34,211,238,0.18),transparent_24%),radial-gradient(circle_at_64%_74%,rgba(45,212,191,0.12),transparent_26%),linear-gradient(160deg,rgba(4,18,23,0.94)_0%,rgba(2,11,15,0.96)_48%,rgba(1,7,10,0.98)_100%)]" />
              <div className="absolute inset-0 rounded-[2.5rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.01)_100%)]" />
              <div className="absolute left-[54%] top-[18%] h-56 w-56 rounded-full bg-red-500/14 blur-3xl" />
              <div className="absolute left-[50%] top-[26%] h-72 w-72 rounded-full bg-teal-400/14 blur-3xl" />
              <div className="absolute left-[58%] top-[62%] h-64 w-64 rounded-full bg-cyan-300/10 blur-3xl" />

              {[...Array(12)].map((_, index) => (
                <div
                  key={`particle-${index}`}
                  className="absolute rounded-full bg-white/35"
                  style={{
                    width: `${index % 3 === 0 ? 3 : 5}px`,
                    height: `${index % 3 === 0 ? 3 : 5}px`,
                    left: `${48 + (index % 6) * 8}%`,
                    top: `${12 + Math.floor(index / 2) * 10}%`,
                    opacity: 0.14 + ((index % 4) * 0.05),
                    filter: 'blur(1px)',
                  }}
                />
              ))}

              <div className="absolute inset-x-[10%] bottom-[16%] h-24 rounded-[999px] bg-teal-300/10 blur-2xl" />
              <div className="absolute inset-x-[18%] bottom-[12%] h-px bg-gradient-to-r from-transparent via-cyan-200/20 to-transparent" />

              <div className="absolute inset-0 z-10 flex items-center justify-center p-6 sm:p-8">
                <img
                  src={heroImage}
                  alt="JumpFroggo banner with frog jumping across lily pads"
                  className="w-full max-w-[360px] rounded-[1.5rem] border border-white/10 object-cover shadow-2xl shadow-black/40 drop-shadow-[0_24px_60px_rgba(0,0,0,0.45)] transition-transform duration-300 hover:scale-[1.01] sm:max-w-[520px] lg:max-w-[620px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="mb-12 text-center">
            <div className="inline-flex items-center space-x-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
              <span className="text-sm font-semibold text-red-400 font-display">Features</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              Built for quick fun and <span className="text-red-500">repeat play</span>
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {featureCards.map((feature, index) => (
              <div
                key={feature.title}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-8 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/0 opacity-0 transition-opacity duration-500 group-hover:from-red-500/10 group-hover:to-transparent group-hover:opacity-100" />
                <feature.icon className="relative text-5xl text-red-500 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6" />
                <h3 className="relative mt-6 font-display text-2xl font-bold text-white">{feature.title}</h3>
                <p className="relative mt-3 leading-relaxed text-gray-400">{feature.description}</p>
                <div className="absolute right-0 top-0 h-20 w-20 rounded-bl-3xl bg-gradient-to-br from-red-500/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="inline-flex items-center space-x-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="text-sm font-semibold text-red-400 font-display">Gameplay Experience</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                Experience the <span className="text-red-500">Gameplay</span>
              </h2>
            </div>
            <p className="max-w-xl text-base leading-relaxed text-gray-400 sm:text-lg">
              Jump across endless floating leaves and challenge your reflexes in a smooth and addictive gameplay
              experience.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {gameplayHighlights.map((item, index) => (
              <div
                key={item.title}
                className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-8 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 via-transparent to-red-500/0 opacity-0 transition-opacity duration-500 group-hover:from-red-500/10 group-hover:to-red-500/5 group-hover:opacity-100" />
                <div className="relative rounded-[1.5rem] border border-red-500/20 bg-[radial-gradient(circle_at_top,_rgba(255,85,85,0.18),_transparent_55%),linear-gradient(180deg,rgba(20,20,22,0.95)_0%,rgba(8,8,9,0.95)_100%)] p-8">
                  <div className="mb-8 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-red-500/30 bg-red-500/10 font-display text-lg font-bold text-red-400">
                    0{index + 1}
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">{item.title}</h3>
                  <p className="mt-4 text-lg leading-relaxed text-gray-400">{item.description}</p>
                  <div className="mt-8 h-1.5 w-24 rounded-full bg-gradient-to-r from-red-500 to-red-300" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 pb-20 relative z-10">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/0 px-6 py-12 text-center shadow-2xl shadow-black/20 backdrop-blur-xl sm:px-10 sm:py-16">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,85,85,0.22),_transparent_55%)]" />
            <div className="absolute -top-12 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-red-500/20 blur-3xl" />
            <div className="relative">
              <h2 className="font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
                Download Now &amp; Start Jumping!
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-400">
                Launch into a vibrant endless hopping challenge with a polished mobile-first experience and RedRender's
                premium presentation.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <a
                  href={playStoreUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 rounded-full border border-red-400/40 bg-gradient-to-r from-red-500 via-red-500 to-red-600 px-8 py-4 font-semibold text-white shadow-[0_0_30px_rgba(239,68,68,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(239,68,68,0.5)]"
                >
                  <FaDownload />
                  <span>Download on Play Store</span>
                </a>
                <Link
                  to="/jumpfroggo-privacy-policy"
                  className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-8 py-4 font-semibold text-white transition-all duration-300 hover:border-cyan-300/50 hover:text-cyan-100"
                >
                  <FaShieldAlt />
                  <span>Privacy Policy</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JumpFroggo;
